import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Finds the "own node" element for each direct child of a tier's row
 * (Prompt 137R): a leaf child (Agent tier, or a ghost create-link) IS the
 * `.wx-oc-node` itself; a branching child (Super Agent/PLC tier) wraps its
 * own node plus a further nested branch inside a `.wx-oc-cluster`, so only
 * that cluster's OWN first-level node (never a descendant several tiers
 * down) is the thing this row's connector should point at. Plain
 * `element.children` indexing (not a CSS selector) so this never depends on
 * a selector engine's exact `:scope`/combinator support in every browser/
 * test runtime.
 */
function getDirectChildNodeElements(rowEl) {
  if (!rowEl) {
    return [];
  }

  const elements = [];
  for (const child of rowEl.children) {
    if (child.classList.contains("wx-oc-node")) {
      elements.push(child);
    } else if (child.classList.contains("wx-oc-cluster")) {
      const ownNode = child.children[0];
      if (ownNode && ownNode.classList.contains("wx-oc-node")) {
        elements.push(ownNode);
      }
    }
  }
  return elements;
}

/**
 * Pure geometry (Prompt 137R): given the connector's own real rendered rect
 * and the real rendered rect of every child it must connect to, returns a
 * vertical trunk from the connector's own horizontal center (the parent
 * node sits centered directly above this connector via flexbox, so the
 * connector's own center IS the parent's center - no separate parent
 * measurement needed), a horizontal bus spanning the outermost child
 * centers, and one vertical drop landing EXACTLY on each child's own
 * measured horizontal center. Every coordinate is a real pixel offset from
 * `connectorRect.left`/`.top`, not a percentage/fraction of an assumed
 * equal-width track - this is what makes every drop line land on its
 * target regardless of unequal branch widths, text-driven node width
 * differences, or any other layout detail this function never has to know
 * about. Takes plain `{left, top, width, height}`-shaped rect objects (not
 * live DOMRects) so it is trivially unit-testable with hand-built fixtures.
 */
export function computeConnectorGeometry(connectorRect, childRects) {
  if (!connectorRect || childRects.length === 0) {
    return { width: connectorRect ? connectorRect.width : 0, height: connectorRect ? connectorRect.height : 0, trunk: null, bus: null, drops: [] };
  }

  const width = connectorRect.width;
  const height = connectorRect.height;
  const trunkX = width / 2;
  const busY = height * 0.6;
  const centers = childRects.map((rect) => rect.left + rect.width / 2 - connectorRect.left);
  const minCenter = Math.min(...centers);
  const maxCenter = Math.max(...centers);

  return {
    width,
    height,
    trunk: { x1: trunkX, y1: 0, x2: trunkX, y2: busY },
    bus: centers.length > 1 ? { x1: minCenter, y1: busY, x2: maxCenter, y2: busY } : null,
    drops: centers.map((x) => ({ x1: x, y1: busY, x2: x, y2: height })),
  };
}

function geometryEquals(a, b) {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Renders one tier's parent -> children connector as a pixel-space SVG
 * overlay, positioned by REAL DOM measurement of its next sibling row's own
 * node elements (never a percentage/track-count guess) - see
 * computeConnectorGeometry's own doc comment for why. Re-measures after
 * every commit (data changes, view-mode changes, ...) via useLayoutEffect,
 * and again on element/window resize via ResizeObserver so a reflow from
 * font loading, a browser resize, or a sibling branch's own height change
 * never leaves a connector pointing at a stale position. `geometryEquals`
 * guards the state update so a no-op re-measurement never triggers another
 * render (which would otherwise re-run this same effect and risk a loop).
 */
export default function OrgConnectorLayer() {
  const svgRef = useRef(null);
  const [geometry, setGeometry] = useState(null);

  const measure = useCallback(() => {
    const svgEl = svgRef.current;
    if (!svgEl) {
      return;
    }
    const connectorRect = svgEl.getBoundingClientRect();
    const rowEl = svgEl.nextElementSibling;
    const childRects = getDirectChildNodeElements(rowEl).map((el) => el.getBoundingClientRect());
    const next = computeConnectorGeometry(connectorRect, childRects);
    setGeometry((previous) => (geometryEquals(previous, next) ? previous : next));
  }, []);

  useLayoutEffect(() => {
    measure();
  });

  useEffect(() => {
    const svgEl = svgRef.current;
    if (!svgEl || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(svgEl);
    if (svgEl.nextElementSibling) {
      resizeObserver.observe(svgEl.nextElementSibling);
    }
    window.addEventListener("resize", measure);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure]);

  return (
    <svg ref={svgRef} className="wx-oc-connector" viewBox={geometry ? `0 0 ${geometry.width} ${geometry.height}` : undefined} aria-hidden="true">
      {geometry?.trunk && <line className="wx-oc-connector-line" x1={geometry.trunk.x1} y1={geometry.trunk.y1} x2={geometry.trunk.x2} y2={geometry.trunk.y2} />}
      {geometry?.bus && <line className="wx-oc-connector-line" x1={geometry.bus.x1} y1={geometry.bus.y1} x2={geometry.bus.x2} y2={geometry.bus.y2} />}
      {geometry?.drops.map((drop, index) => (
        <line key={index} className="wx-oc-connector-line" x1={drop.x1} y1={drop.y1} x2={drop.x2} y2={drop.y2} />
      ))}
    </svg>
  );
}
